<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-center">
      <div class="col-lg-10 col-md-12">
        <div class="card">
          <div class="card-body p-4">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading details...</p>
            </div>

            <template v-else>
            <!-- Assignment Details Box -->
            <div class="border border-dark p-4 mb-4 text-sm rounded" v-if="displayAssignmentDetails">
              <div class="mb-2">
                <strong>LOCATION:</strong> {{ displayAssignmentDetails.location }}
              </div>
              <div class="mb-2">
                <strong>YOUR EMAIL:</strong> {{ displayAssignmentDetails.email }}
              </div>
              <div class="mb-2">
                <strong>ASSIGNMENT GROUP:</strong> {{ displayAssignmentDetails.assignmentGroup }}
              </div>
              <div class="mb-2">
                <strong>ASSIGNMENT GROUP ID:</strong> {{ displayAssignmentDetails.assignmentGroupId }}
              </div>
              <div class="mb-0">
                <strong>ASSIGNMENT DATE:</strong> {{ displayAssignmentDetails.assignmentDate }}
              </div>
            </div>

            <!-- User Greeting -->
            <div class="mb-4">
              <p class="mb-2">Hello <strong>{{ displayUserName }}</strong>,</p>
            </div>

            <!-- Confirmation Question -->
            <div class="mb-4">
              <!-- Unavailable Notice -->
              <div v-if="unavailable" class="alert alert-warning d-flex align-items-center gap-2 mb-3">
                <i class="material-icons">info</i>
                <span>{{ unavailableMessage }}</span>
              </div>
              <!-- Show result after decline action -->
              <div v-else-if="declineSuccess" class="alert alert-success d-flex align-items-center gap-2 mb-3">
                <i class="material-icons">check_circle</i>
                <span>{{ declineSuccessMsg }}</span>
              </div>
              <div v-else-if="declineError" class="alert alert-danger d-flex align-items-center gap-2 mb-3">
                <i class="material-icons">error</i>
                <span>{{ declineErrorMsg }}</span>
              </div>

              <template v-if="!declineSuccess && !unavailable">
                <p class="text-danger fw-bold mb-3" style="font-size: 1.1rem;">
                  Are you sure you want to decline this assignment?
                </p>
                
                <!-- Confirm Decline Button -->
                <div class="mb-4">
                  <MaterialButton 
                    color="primary" 
                    :disabled="declining"
                    @click="confirmDecline"
                  >
                    {{ declining ? 'Processing...' : 'Click here to confirm decline' }}
                  </MaterialButton>
                </div>
              </template>
            </div>

            <!-- Divider -->
            <hr class="my-4" />

            <!-- Did You Know Section -->
            <div class="mb-4" v-if="showDidYouKnow">
              <h5 class="fw-bold mb-3">Did You Know?</h5>
              <p class="mb-3">{{ displayDidYouKnowText }}</p>
              <ul class="mb-3" v-if="displayDidYouKnowSteps && displayDidYouKnowSteps.length > 0">
                <li v-for="(step, index) in displayDidYouKnowSteps" :key="index" class="mb-2">{{ step }}</li>
              </ul>
            </div>

            <!-- Login Link -->
            <router-link to="/sign-in">
              <MaterialButton color="success">
                Click here to Login
              </MaterialButton>
            </router-link>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import MaterialButton from '@/components/common/MaterialButton.vue'
import api from '@/config/api'

const route = useRoute()
const toast = useToast()

// State
const loading = ref(true)
const declining = ref(false)
const declineSuccess = ref(false)
const declineSuccessMsg = ref('')
const declineError = ref(false)
const declineErrorMsg = ref('')
const unavailable = ref(false)
const unavailableMessage = ref('')
const assignmentDetails = ref({
  location: '',
  email: '',
  assignmentGroup: '',
  assignmentGroupId: '',
  assignmentDate: ''
})
const userName = ref('')
const showDidYouKnow = ref(true)
const didYouKnowText = ref('')
const didYouKnowSteps = ref([])

// Mock fallback data
const mockAssignmentDetails = {
  location: 'CS Test Church -> Chicago main Branch',
  email: 'Konadu_A_552@nomail.churchscheduling.com',
  assignmentGroup: 'Usher @ Sunday Morning Service-main',
  assignmentGroupId: '39674',
  assignmentDate: 'Dec 28, 2015 10:00:00 AM'
}
const mockUserName = 'Konadu'
const mockDidYouKnowText = 'It is highly recommended that all users respond to assignments. Providing a response ensures the following:'
const mockDidYouKnowSteps = [
  "All administrators are made aware that you have seen the assignment.",
  "If you decline, there is enough time to assign another person.",
  "Administrators do not have to take additional time to follow up with you and can focus on other duties."
]

// Computed display values (prefer API, fallback to mock)
const displayAssignmentDetails = computed(() =>
  assignmentDetails.value && Object.values(assignmentDetails.value).some(v => v)
    ? assignmentDetails.value
    : mockAssignmentDetails
)
const displayUserName = computed(() => userName.value || mockUserName)
const displayDidYouKnowText = computed(() => didYouKnowText.value || mockDidYouKnowText)
const displayDidYouKnowSteps = computed(() =>
  (didYouKnowSteps.value && didYouKnowSteps.value.length > 0) ? didYouKnowSteps.value : mockDidYouKnowSteps
)

// Fetch page details on mount
const fetchDeclineDetails = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString) {
    loading.value = false
    return
  }

  try {
    loading.value = true
    const response = await api.get(`/account/declineassignment-details/cs/${encodedString}`)

    if (response.data) {
      const data = response.data

      if (data.assignmentDetails) {
        assignmentDetails.value = {
          location: data.assignmentDetails.location || '',
          email: data.assignmentDetails.email || '',
          assignmentGroup: data.assignmentDetails.assignment_group || '',
          assignmentGroupId: data.assignmentDetails.assignment_group_id || '',
          assignmentDate: data.assignmentDetails.assignment_date || ''
        }
      }

      userName.value = data.username || ''
      if (data.unavailable) {
        unavailable.value = true
        unavailableMessage.value = data.message || 'This assignment is no longer available.'
      }
      if (data.showDidYouKnow != null) showDidYouKnow.value = data.showDidYouKnow
      if (data.didYouKnowText != null) didYouKnowText.value = data.didYouKnowText
      if (data.didYouKnowSteps && Array.isArray(data.didYouKnowSteps)) didYouKnowSteps.value = data.didYouKnowSteps
    }
  } catch (error) {
    console.error('Error fetching decline assignment details:', error)
  } finally {
    loading.value = false
  }
}

// Confirm decline action
const confirmDecline = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString || declining.value) return

  declining.value = true
  declineError.value = false
  declineErrorMsg.value = ''
  try {
    const response = await api.post(`/account/declineassignment/cs/${encodedString}`)
    if (response.data?.unavailable) {
      unavailable.value = true
      unavailableMessage.value = response.data.message || 'This assignment is no longer available.'
      return
    }
    const msg = response.data?.message || 'Assignment declined successfully.'
    declineSuccessMsg.value = msg
    declineSuccess.value = true
    toast.success(msg, { position: 'top-right' })
  } catch (error) {
    console.error('Error declining assignment:', error)
    const errData = error.response?.data
    if (errData?.unavailable) {
      unavailable.value = true
      unavailableMessage.value = errData.message || 'This assignment is no longer available.'
      return
    }
    const errMsg = errData?.message || error.message || 'Failed to decline assignment.'
    declineErrorMsg.value = errMsg
    declineError.value = true
    toast.error(errMsg, { position: 'top-right' })
  } finally {
    declining.value = false
  }
}

onMounted(async () => {
  await fetchDeclineDetails()
})
</script>

